import styled, { keyframes } from "styled-components";

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

export const PolicyPageWrapper = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
`;

export const PolicyHeroSection = styled.section`
  position: relative;
  padding: 120px 40px 100px;
  overflow: hidden;
  background: var(--bg-secondary);

  .heroBg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;

    .gradientOrb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);

      &.orb1 {
        width: 500px;
        height: 500px;
        background: radial-gradient(
          circle,
          rgba(217, 58, 53, 0.15) 0%,
          transparent 70%
        );
        top: -200px;
        right: -100px;
        animation: ${pulse} 8s ease-in-out infinite;
      }

      &.orb2 {
        width: 400px;
        height: 400px;
        background: radial-gradient(
          circle,
          rgba(43, 59, 124, 0.15) 0%,
          transparent 70%
        );
        bottom: -150px;
        left: -100px;
        animation: ${pulse} 10s ease-in-out infinite reverse;
      }
    }

    .gridPattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: linear-gradient(
          var(--text-primary) 1px,
          transparent 1px
        ),
        linear-gradient(90deg, var(--text-primary) 1px, transparent 1px);
      background-size: 60px 60px;
      opacity: 0.02;
    }
  }

  .floatingShape {
    position: absolute;
    border-radius: 20px;
    animation: ${float} 6s ease-in-out infinite;

    &.shape1 {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, var(--accent) 0%, transparent 100%);
      top: 15%;
      left: 8%;
      opacity: 0.3;
      animation-delay: 0s;
    }

    &.shape2 {
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, var(--primary) 0%, transparent 100%);
      top: 25%;
      right: 12%;
      opacity: 0.25;
      animation-delay: 2s;
      border-radius: 50%;
    }

    &.shape3 {
      width: 100px;
      height: 100px;
      border: 3px solid var(--accent);
      background: transparent;
      bottom: 15%;
      right: 8%;
      opacity: 0.2;
      animation-delay: 4s;
      border-radius: 50%;
    }
  }

  .heroContent {
    max-width: 900px;
    margin: 0 auto;
    text-align: center;
    position: relative;
    z-index: 1;

    .heroLabel {
      display: inline-flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 28px;

      .line {
        width: 40px;
        height: 2px;
        background: var(--accent);
      }

      span:not(.line) {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 3px;
        color: var(--accent);
      }
    }

    h1 {
      font-family: Poppins;
      font-size: 72px;
      font-weight: 800;
      line-height: 1.1;
      color: var(--text-primary);
      margin: 0 0 24px;

      .highlight {
        background: linear-gradient(
          135deg,
          var(--accent) 0%,
          var(--primary) 100%
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }

    .heroDescription {
      font-family: Poppins;
      font-size: 20px;
      line-height: 1.7;
      color: var(--text-secondary);
      max-width: 700px;
      margin: 0 auto 40px;
    }

    .heroBadges {
      display: flex;
      justify-content: center;
      gap: 24px;
      flex-wrap: wrap;

      .badge {
        display: flex;
        align-items: center;
        gap: 12px;
        background: var(--card-bg);
        padding: 16px 28px;
        border-radius: 16px;
        border: 2px solid var(--card-border);

        .badgeIcon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(
            135deg,
            rgba(217, 58, 53, 0.1) 0%,
            rgba(43, 59, 124, 0.1) 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            width: 22px;
            height: 22px;
            color: var(--accent);
          }
        }

        .badgeText {
          text-align: left;

          .badgeNumber {
            font-family: Poppins;
            font-size: 24px;
            font-weight: 800;
            color: var(--text-primary);
          }

          .badgeLabel {
            font-family: Poppins;
            font-size: 13px;
            color: var(--text-secondary);
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 100px 20px 80px;

    .heroContent {
      h1 {
        font-size: 42px;
      }

      .heroDescription {
        font-size: 16px;
      }

      .heroBadges {
        flex-direction: column;
        align-items: center;

        .badge {
          width: 100%;
          max-width: 280px;
        }
      }
    }

    .floatingShape {
      display: none;
    }
  }
`;

export const PolicyContainer = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: 100px 40px 120px;
  background: var(--bg-primary);
  max-width: 1200px;
  margin: 0 auto;
  gap: 80px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
    gap: 60px;
  }
`;

export const AboutSection = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 0.4fr 0.6fr;
  gap: 60px;
  align-items: start;

  .aboutLeft {
    .sectionLabel {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;

      .line {
        width: 30px;
        height: 3px;
        background: var(--accent);
        border-radius: 2px;
      }

      span {
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 2px;
        color: var(--accent);
      }
    }

    h2 {
      font-family: Poppins;
      font-size: 48px;
      font-weight: 800;
      line-height: 1.15;
      color: var(--text-primary);
      margin: 0;

      .highlight {
        background: linear-gradient(
          135deg,
          var(--accent) 0%,
          var(--primary) 100%
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    }
  }

  .aboutRight {
    .leadParagraph {
      font-family: Poppins;
      font-size: 20px;
      line-height: 1.8;
      color: var(--text-secondary);
      margin: 0 0 24px;
      padding-left: 24px;
      border-left: 4px solid var(--accent);
    }

    .contentParagraph {
      font-family: Poppins;
      font-size: 17px;
      line-height: 1.9;
      color: var(--text-secondary);
      margin: 0;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;

    .aboutLeft h2 {
      font-size: 36px;
    }

    .aboutRight {
      .leadParagraph {
        font-size: 17px;
      }
    }
  }
`;

export const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ValueCard = styled.div`
  background: var(--card-bg);
  border-radius: 24px;
  padding: 40px;
  border: 2px solid var(--card-border);
  will-change: transform, opacity;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--accent) 0%, var(--primary) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: var(--accent);
    box-shadow: 0 25px 60px var(--shadow-medium);

    &::before {
      opacity: 1;
    }
  }

  &.full-width {
    grid-column: 1 / -1;
  }

  .cardHeader {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
  }

  .icon {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    background: linear-gradient(
      135deg,
      rgba(217, 58, 53, 0.1) 0%,
      rgba(43, 59, 124, 0.1) 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 30px;
      height: 30px;
      color: var(--accent);
    }
  }

  h4 {
    color: var(--text-primary);
    font-family: Poppins;
    font-size: 28px;
    font-weight: 700;
    margin: 0;
  }

  p {
    color: var(--text-secondary);
    font-family: Poppins;
    font-size: 16px;
    line-height: 1.8;
    margin: 0;
  }

  .core-values-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-top: 32px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    padding: 28px;

    h4 {
      font-size: 22px;
    }
  }
`;

export const CoreValueItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 2px solid var(--card-border);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary);
    transform: translateY(-4px);
  }

  .valueIcon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(
      135deg,
      rgba(43, 59, 124, 0.15) 0%,
      rgba(217, 58, 53, 0.15) 100%
    );
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 8px;

    svg {
      width: 24px;
      height: 24px;
      color: var(--primary);
    }
  }

  .value-title {
    color: var(--text-primary);
    font-family: Poppins;
    font-size: 18px;
    font-weight: 700;
  }

  .value-desc {
    color: var(--text-secondary);
    font-family: Poppins;
    font-size: 14px;
    line-height: 1.6;
  }
`;

export const PolicyHighlight = styled.div`
  width: 100%;
  background: linear-gradient(135deg, var(--primary) 0%, #1e2a5e 100%);
  border-radius: 32px;
  padding: 64px;
  text-align: center;
  position: relative;
  overflow: hidden;
  will-change: transform, opacity;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -50px;
    right: -50px;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      rgba(217, 58, 53, 0.3) 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  .policyContent {
    position: relative;
    z-index: 1;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    padding: 10px 24px;
    border-radius: 30px;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 28px;
    backdrop-filter: blur(10px);
  }

  h4 {
    color: #fff;
    font-family: Poppins;
    font-size: 48px;
    font-weight: 800;
    margin: 0 0 24px;
    line-height: 1.2;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-family: Poppins;
    font-size: 18px;
    line-height: 1.8;
    max-width: 700px;
    margin: 0 auto 36px;
  }

  .ctaButton {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 16px 36px;
    background: var(--accent);
    color: #fff;
    font-family: Poppins;
    font-size: 16px;
    font-weight: 600;
    border-radius: 14px;
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(217, 58, 53, 0.4);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 768px) {
    padding: 40px 24px;
    border-radius: 24px;

    h4 {
      font-size: 32px;
    }

    p {
      font-size: 16px;
    }
  }
`;
